import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CaretDown } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';

interface Opcion {
  value: string;
  label: string;
}

interface Props {
  /** Prefijo único para los ids de los checkboxes (evita colisiones entre instancias). */
  name: string;
  placeholder: string;
  opciones: Opcion[];
  seleccionados: string[];
  onChange: (valores: string[]) => void;
  className?: string;
  /**
   * Modo "excluir": sin selección explícita se consideran TODAS las opciones
   * marcadas (útil para listas largas, ej. proveedores) — destildar una la
   * excluye en vez de partir de "ninguna marcada". Usa la misma convención
   * de filtrado (array vacío = sin filtro) por debajo; solo cambia cómo se
   * muestran los checkboxes y qué hace destildar el primero.
   */
  todosPorDefecto?: boolean;
  /** Muestra una caja de búsqueda arriba de la lista (útil con muchas opciones). */
  conBusqueda?: boolean;
}

/**
 * Dropdown de selección múltiple con checkboxes. Usado para filtros donde
 * el usuario puede querer combinar varios valores a la vez (términos de
 * pago, buckets de vencimiento, proveedores), a diferencia de un <Select>
 * de valor único.
 */
/**
 * Valor reservado para representar "ninguno seleccionado" en modo `todosPorDefecto`, donde un
 * array vacío ya significa "todos" (convención usada por los filtros que consumen este
 * componente: array vacío = sin filtro = todos). Ningún código real (VendorCode, etc.) coincide
 * con este valor, así que del lado del filtro funciona solo: `.includes(vendorCode)` da `false`
 * para cualquier factura real, excluyéndolas a todas sin tocar la lógica de los llamadores.
 */
const NINGUNO_SELECCIONADO = '__ninguno_seleccionado__';

export function MultiSelect({
  name,
  placeholder,
  opciones,
  seleccionados,
  onChange,
  className,
  todosPorDefecto = false,
  conBusqueda = false,
}: Props) {
  const [busqueda, setBusqueda] = useState('');

  const todosMarcados = todosPorDefecto && seleccionados.length === 0;
  const ningunoMarcado =
    todosPorDefecto && seleccionados.length === 1 && seleccionados[0] === NINGUNO_SELECCIONADO;

  function estaMarcado(value: string) {
    if (ningunoMarcado) return false;
    return todosMarcados || seleccionados.includes(value);
  }

  function alternar(value: string) {
    if (todosMarcados) {
      // Partir de "todos marcados" implícito: destildar una la excluye del resto.
      onChange(opciones.map((o) => o.value).filter((v) => v !== value));
      return;
    }
    if (ningunoMarcado) {
      // Partir de "ninguno marcado": tildar una la vuelve la única incluida.
      onChange([value]);
      return;
    }
    if (seleccionados.includes(value)) {
      onChange(seleccionados.filter((v) => v !== value));
    } else {
      onChange([...seleccionados, value]);
    }
  }

  const opcionesFiltradas = useMemo(() => {
    if (!busqueda.trim()) return opciones;
    const norm = busqueda.trim().toLowerCase();
    return opciones.filter((o) => o.label.toLowerCase().includes(norm));
  }, [opciones, busqueda]);

  const etiqueta = todosMarcados
    ? placeholder
    : ningunoMarcado
      ? 'Ninguno seleccionado'
      : seleccionados.length === 0
        ? placeholder
        : seleccionados.length === 1
          ? (opciones.find((o) => o.value === seleccionados[0])?.label ?? placeholder)
          : todosPorDefecto && seleccionados.length === opciones.length
            ? placeholder
            : `${seleccionados.length} seleccionados`;

  return (
    // modal: ver comentario en VendorPickerBC — evita que un Dialog contenedor le
    // robe el foco al Popover (y con él, el scroll/búsqueda de la lista).
    <Popover modal>
      <PopoverTrigger asChild>
        <Button variant="outline" className={className ?? 'w-[200px] justify-between font-normal'}>
          <span className="truncate">{etiqueta}</span>
          <CaretDown className="h-4 w-4 shrink-0 opacity-50" weight="bold" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-2" align="start">
        {conBusqueda && (
          <Input
            placeholder="Buscar..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="mb-2"
          />
        )}
        <div className="max-h-64 space-y-1 overflow-auto">
          {opcionesFiltradas.length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">Sin opciones</p>
          ) : (
            opcionesFiltradas.map((o) => (
              <div
                key={o.value}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              >
                <Checkbox
                  id={`multi-select-${name}-${o.value}`}
                  checked={estaMarcado(o.value)}
                  onCheckedChange={() => alternar(o.value)}
                />
                <Label
                  htmlFor={`multi-select-${name}-${o.value}`}
                  className="cursor-pointer truncate font-normal"
                >
                  {o.label}
                </Label>
              </div>
            ))
          )}
        </div>
        {todosPorDefecto ? (
          todosMarcados ? (
            <Button
              variant="ghost"
              size="sm"
              className="mt-1 w-full text-muted-foreground"
              onClick={() => onChange([NINGUNO_SELECCIONADO])}
            >
              Deseleccionar todos
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="mt-1 w-full text-muted-foreground"
              onClick={() => onChange([])}
            >
              Marcar todos
            </Button>
          )
        ) : (
          seleccionados.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-1 w-full text-muted-foreground"
              onClick={() => onChange([])}
            >
              Limpiar selección
            </Button>
          )
        )}
      </PopoverContent>
    </Popover>
  );
}
